// ResourceTag — AppLogo + item name pair used in task subjects.
// Mirrors `.resource-tag` in /Users/harry/Downloads/employee-portal/project/styles.css
// (lines 2451–2462) and the ResourceTag component in components.jsx (line 88):
//   <span resource-tag>
//     <span resource-tag__logo><AppLogoSquare /></span>
//     <span resource-tag__name>{name}</span>
//   </span>
//
// Spec: inline-flex, gap 8px (`sm`), font-size 14px (`md`) / 500 / neutral.950,
// no wrap, with the name set at line-height 1 so it sits flush against the
// logo. The logo is fixed pixel size — `md` = 24×24 (the design default), `sm`
// = 20×20. Note that AppLogo's `md` preset is 32×32 and `sm` is 20×20, so for
// the 24px case we render a custom inline tile that mirrors AppLogoSquare in
// components.jsx (line 103).

import { Box, Image } from "@chakra-ui/react";

import AppLogo from "./AppLogo";

export interface ResourceTagProps {
  /** App name — feeds the AppLogo abbreviation when no logo URL is set. */
  appName: string;
  /** Optional brand logo URL for the AppLogo. */
  appLogoUrl?: string;
  /** Optional fallback background color for the AppLogo abbreviation tile. */
  appBg?: string;
  /** Item / resource name displayed next to the logo. */
  itemName: string;
  /** Logo size — sm = 20×20, md = 24×24 (default). */
  size?: "sm" | "md";
}

// Inline 24×24 logo tile — mirrors `AppLogoSquare({ size: 24 })` from
// components.jsx. We can't reuse <AppLogo> for the 24px case because its
// `md` preset is 32×32.
function ResourceLogo24({
  appName,
  appLogoUrl,
  appBg,
}: {
  appName: string;
  appLogoUrl?: string;
  appBg?: string;
}) {
  const px = 24;
  const pad = Math.round(px * 0.18); // 4px
  const inner = px - pad * 2; // 16px
  const radius = Math.round(px * 0.22); // 5px

  if (appLogoUrl) {
    return (
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        overflow="hidden"
        width={`${px}px`}
        height={`${px}px`}
        borderRadius={`${radius}px`}
        bg="white"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="neutral.200"
      >
        <Image
          src={appLogoUrl}
          alt={appName}
          width={`${inner}px`}
          height={`${inner}px`}
          display="block"
        />
      </Box>
    );
  }

  const abbr = (appName?.trim() || "?").slice(0, 2).toUpperCase();
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      overflow="hidden"
      width={`${px}px`}
      height={`${px}px`}
      borderRadius={`${radius}px`}
      bg={appBg || "neutral.200"}
      color="white"
      fontFamily="inherit"
      fontWeight={700}
      fontSize="10px"
    >
      {abbr}
    </Box>
  );
}

const ResourceTag = ({
  appName,
  appLogoUrl,
  appBg,
  itemName,
  size = "md",
}: ResourceTagProps) => {
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      gap="sm"
      fontSize="md"
      fontWeight={500}
      color="neutral.950"
      whiteSpace="nowrap"
    >
      <Box as="span" display="inline-flex" flexShrink={0}>
        {size === "sm" ? (
          <AppLogo
            name={appName}
            logoUrl={appLogoUrl}
            bg={appBg}
            size="sm"
          />
        ) : (
          <ResourceLogo24
            appName={appName}
            appLogoUrl={appLogoUrl}
            appBg={appBg}
          />
        )}
      </Box>
      <Box as="span" lineHeight={1}>
        {itemName}
      </Box>
    </Box>
  );
};

ResourceTag.displayName = "ResourceTag";

export default ResourceTag;
